import { CardContent, RadioGroup, TextField, Typography } from "@mui/material";
import { MprcCard, MprcContainer, MprcFormSubHeader } from "components";
import { MprcRadio } from "components/MprcRadio";
import { MprcQuillContainer, QuillCommands } from "components/Quill";
import { BroadcastMessageType, MailModelPropertyDefinition, UserOperationBroadcastMessageProps } from "generated/CoreApi_v1";
import { useReferredState } from "hooks/useReferredState";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { BroadcastMessageStepProps } from "views/foundation/broadcastMessage/Home";
import { useBroadcastMessageContext } from "views/foundation/broadcastMessage/Home/StepManagement";
import { TemplateEditor } from "views/foundation/notification/components/TemplateEditor";

interface BroadcastMessage extends UserOperationBroadcastMessageProps {
  bodyText?: string;
}

const StepEditMessageComponent: React.FC<BroadcastMessageStepProps> = ({ setCanSave }) => {

  const { t } = useTranslation();
  const ctx = useBroadcastMessageContext();

  const [localMessage, setLocalMessage, localMessageRef] = useReferredState<BroadcastMessage>(ctx.message);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const editorRef = useRef<QuillCommands | null>(null);
  const initialBodyMessage = useRef<string | null>(ctx.message.body);

  ///
  const syncRecord = (newMessage: BroadcastMessage) => {
    setCanSave(((newMessage.subject ?? "").length > 0 && (newMessage.bodyText ?? "").replace("\n", "").trim().length > 0)
      || newMessage.type == BroadcastMessageType.WelcomeMessage);

    ctx.setMessage(newMessage);
  };

  ///
  const toggleMessageType = (type: BroadcastMessageType) => {
    updateLocalRecord({ type: type });
    //Armazena a mensagem para restaurar se o usuário voltar para o tipo CustomMessage
    initialBodyMessage.current = localMessageRef.current.body;
  };

  ///
  const updateLocalRecord = (partialMessage: Partial<BroadcastMessage>) => {
    var newMessage = { ...localMessageRef.current, ...partialMessage };
    setLocalMessage(newMessage);

    // Limpa o timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Cria novo timer para atualizar o contexto após 300ms
    debounceTimerRef.current = setTimeout(() => {
      syncRecord(newMessage);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return <MprcCard>
    <CardContent>
      <MprcContainer>
        <MprcFormSubHeader
          title={t("BroadcastMessage.StepEdit.Title")}
          subtitle={t("BroadcastMessage.StepEdit.HintText")}
        >
        </MprcFormSubHeader>
      </MprcContainer>
      <MprcContainer>
        <RadioGroup row value={localMessage.type} onChange={(_, value) => toggleMessageType(parseInt(value) as BroadcastMessageType)}>
          <MprcRadio value={BroadcastMessageType.CustomMessage} label={t("BroadcastMessage.StepEdit.CustomMessageOption")} />
          <MprcRadio value={BroadcastMessageType.WelcomeMessage} label={t("BroadcastMessage.StepEdit.WelcomeMessageOption")} />
        </RadioGroup>
        {localMessage.type == BroadcastMessageType.WelcomeMessage &&
          <Typography variant="subtitle1" color="warning">{t("BroadcastMessage.StepEdit.WelcomeMessageHint")}</Typography>
        }
      </MprcContainer>

      {localMessage.type == BroadcastMessageType.CustomMessage &&
        <>
          <MprcContainer>
            <TextField
              autoFocus
              fullWidth
              label={t("BroadcastMessage.StepEdit.SubjectLabel")}
              error={localMessage.type == BroadcastMessageType.CustomMessage && localMessage.subject?.length == 0}
              value={localMessage.subject}
              onChange={e => updateLocalRecord({ subject: e.target.value })}
            />
          </MprcContainer>
          <MprcQuillContainer>
            <TemplateEditor
              toolbar
              minHeight={450}
              ref={editorRef}
              initialValue={initialBodyMessage.current ?? undefined}
              readOnly={false}
              error={localMessage.type == BroadcastMessageType.CustomMessage && localMessage?.bodyText?.replaceAll("\n", "").trim().length == 0}
              placeholder={t("BroadcastMessage.StepEdit.ContentPlaceholder")}
              onChange={(content, delta, source, editor) => updateLocalRecord({ body: editor.getHTML(), bodyText: editor.getText() })}
              templateProperties={[{ propName: "#User.DisplayName", displayName: "Nome do Destinatário" } as MailModelPropertyDefinition]}
            />
          </MprcQuillContainer>
        </>
      }
    </CardContent>
  </MprcCard>;
};

export const StepEditMessage = React.memo(StepEditMessageComponent);